import { IS_DEFINED, UIComponentView, UITextField, UIView } from "uicore-ts"
import { ValueOf } from "uicore-ts/compiledScripts/UIObject"
import { UITextView } from "uicore-ts/compiledScripts/UITextView"
import { BSColor } from "./BSColor"
import { css, RHViewUtils } from "./RHViewUtils"

@UIComponentView
export class RHTextField extends UITextField {
    
    // --- Validation Configuration ---
    
    isRequired: boolean = false
    validationRegex?: RegExp
    validateOnInput: boolean = false
    validateOnBlur: boolean = true
    private hasBeenBlurred: boolean = false
    
    
    // --- Numeric Stepper Configuration ---
    
    private _type: "text" | "number" | "tel" | "email" | "password" = "text"
    private _minValue?: number
    private _maxValue?: number
    
    set type(value: "text" | "number" | "tel" | "email" | "password") {
        const wasNumeric = this._type === "number"
        const isNumeric = value === "number"
        
        this._type = value
        
        this.viewHTMLElement.setAttribute("type", value)
        
        // Sync HTML attributes
        this.updateNativeAttributes()
        
        // Only trigger layout if we actually added/removed buttons previously (which we don't anymore)
        // but kept for consistency if state changes affect layout elsewhere.
        // this.setNeedsLayout()
    }
    
    get type(): "text" | "number" | "tel" | "email" | "password" {
        return this._type
    }
    
    // This property is now read-only and derived, as we rely on native behavior
    get isNumericStepper(): boolean {
        return this._type === "number"
    }
    
    stepValue: number = 1
    
    set minValue(minValue: number | undefined) {
        this._minValue = minValue
        this.updateNativeAttributes()
        this.enforceMinMaxValuesIfNeeded()
    }
    
    get minValue(): number | undefined {
        return this._minValue
    }
    
    set maxValue(maxValue: number | undefined) {
        this._maxValue = maxValue
        this.updateNativeAttributes()
        this.enforceMinMaxValuesIfNeeded()
    }
    
    get maxValue(): number | undefined {
        return this._maxValue
    }
    
    enforceMinMaxValuesIfNeeded() {
        if (this.type == "number") {
            let currentValue = this.text.numericalValue ?? 0
            if (IS_DEFINED(this.minValue)) {
                currentValue = [currentValue, this.minValue].max()
            }
            if (IS_DEFINED(this.maxValue)) {
                currentValue = [
                    currentValue,
                    this.maxValue
                ].min()
            }
            this.text = currentValue + ""
        }
    }
    
    allowDecimals: boolean = false
    
    // --- Constants for styling ---
    private readonly defaultBorderColor = "#ced4da"
    
    // Removed subviews logic as we use native spinners now
    
    constructor(elementID?: string, viewHTMLElement?: null, type?: string | ValueOf<typeof UITextView.type>) {
        
        super(elementID, viewHTMLElement, type)
        
        // Ensure that element has the correct class applied
        this.viewHTMLElement.classList.add(this.styleClassName)
        
        if (type) {
            this._type = type as any
        }
        
        // Initialize native attributes based on initial type
        this.updateNativeAttributes();
        
        // 3. Base Bootstrap Styling
        (this as RHTextField).configureWithObject({
            textColor: BSColor.textBlack,
            backgroundColor: BSColor.textWhite,
            style: {
                display: "block",
                width: "100%",
                padding: "0.375rem 0.75rem",
                fontSize: "1rem",
                fontWeight: "400",
                lineHeight: "1.5",
                backgroundClip: "padding-box",
                border: `1px solid ${this.defaultBorderColor}`,
                borderRadius: "0.25rem",
                transition: "border-color .15s ease-in-out,box-shadow .15s ease-in-out",
                boxSizing: "border-box",
                outline: "none",
                // appearance: "none" is set here for the default clean look,
                // but will be overridden to "auto" for numeric types in the setter.
                appearance: "none"
            }
        })
        
        // 4. Handle Focus State
        this.addTargetForControlEvent(UIView.controlEvent.Focus, () => {
            this.style.borderColor = BSColor.primaryBorderHover.stringValue
            this.style.boxShadow = `0 0 0 0.25rem ${BSColor.primaryShadow.stringValue}`
            this.style.outline = "0"
        })
        
        // 5. Handle Blur State
        this.addTargetForControlEvent(UIView.controlEvent.Blur, () => {
            this.hasBeenBlurred = true
            // We reset to default here before validate in case validate decides not to apply styles
            this.style.borderColor = this.defaultBorderColor
            this.style.boxShadow = "none"
            if (this.validateOnBlur) {
                this.validate()
            }
        })
        
        // 6. Handle Input Changes
        this.addTargetForControlEvent(UITextField.controlEvent.TextChange, () => {
            // If we are currently showing an error, clear it immediately on input
            if (this.style.borderColor === BSColor.danger.stringValue) {
                this.validate()
            }
            else if (this.validateOnInput && this.hasBeenBlurred) {
                this.validate()
            }
        })
        
        // 7. Placeholder Styling
        if (this.elementID) {
            RHViewUtils.injectCSS(css`
                #${this.elementID}::placeholder {
                    color: #6c757d;
                    opacity: 1;
                    user-select: none;
                }
            `, `rh-textfield-style-${this.elementID}`)
        }
    }
    
    /**
     * Updates the native HTML attributes (min, max).
     */
    private updateNativeAttributes() {
        if (this._type === "number") {
            if (this._minValue !== undefined) {
                this.viewHTMLElement.setAttribute("min", this._minValue.toString())
            }
            else {
                this.viewHTMLElement.removeAttribute("min")
            }
            
            if (this._maxValue !== undefined) {
                this.viewHTMLElement.setAttribute("max", this._maxValue.toString())
            }
            else {
                this.viewHTMLElement.removeAttribute("max")
            }
            
            if (this.stepValue) {
                this.viewHTMLElement.setAttribute("step", this.allowDecimals ? "any" : this.stepValue.toString())
            }
        }
        else {
            this.viewHTMLElement.removeAttribute("min")
            this.viewHTMLElement.removeAttribute("max")
        }
    }
    
    
    // --- Core Logic ---
    
    validate(): boolean {
        const currentValue = this.text ?? ""
        let isValid = true
        
        // Check if validation rules are actually configured
        const hasValidationRules = this.isRequired || (this.validationRegex !== undefined)
        
        if (!hasValidationRules) {
            // No rules defined: Return true (logically valid) but reset visuals to neutral
            this.resetValidationVisuals()
            return true
        }
        
        // Rules exist, perform validation logic
        if (this.isRequired && currentValue.length === 0) {
            isValid = false
        }
        
        if (isValid && currentValue.length > 0 && this.validationRegex) {
            if (!this.validationRegex.test(currentValue)) {
                isValid = false
            }
        }
        
        // Use the property setter
        this.isValid = isValid
        return isValid
    }
    
    resetValidationVisuals() {
        // Revert to the default state (gray border, no shadow)
        this.style.borderColor = this.defaultBorderColor
        this.style.boxShadow = "none"
    }
    
    set isValid(value: boolean) {
        if (value) {
            this.style.borderColor = BSColor.success.stringValue
            this.style.boxShadow = "none"
        }
        else {
            this.style.borderColor = BSColor.danger.stringValue
            this.style.boxShadow = "none"
        }
    }
    
    override updateContentForCurrentEnabledState() {
        
        if (this.enabled) {
            // --- ENABLED STATE ---
            this.alpha = 1
            this.textColor = BSColor.textBlack
            this.backgroundColor = BSColor.textWhite
            
            this.validate()
            this.enforceMinMaxValuesIfNeeded()
            
            this.style.cursor = "text"
        }
        else {
            // --- DISABLED STATE ---
            this.alpha = 1
            this.textColor = BSColor.secondary
            this.backgroundColor = BSColor.disabled
            
            this.style.borderColor = this.defaultBorderColor
            this.style.boxShadow = "none"
            this.style.cursor = "not-allowed"
        }
        
        
        this.userInteractionEnabled = this.enabled
    }
    
}
